import { Link, type LinkProps } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import type { ReactNode } from 'react';

import { Button } from '@nucleus-commerce/ui/components/button';

import { TourButton } from '../tour/components/tour-button';

export interface PageHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  /** Right-aligned actions, usually one primary Button. */
  actions?: ReactNode;
  /** Back link props, e.g. { to: '/s/$storeId/products', params }. */
  backTo?: LinkProps | undefined;
  backLabel?: string;
  /** Status badge or similar rendered next to the title. */
  meta?: ReactNode;
  /** `data-tour` value of the header itself. */
  tour?: string;
}

export function PageHeader({
  title,
  description,
  actions,
  backTo,
  backLabel = 'Back',
  meta,
  tour,
}: PageHeaderProps) {
  return (
    <header
      data-tour={tour}
      data-slot="page-header"
      className="flex flex-col gap-3 pb-4 md:flex-row md:items-start md:justify-between"
    >
      <div className="min-w-0 space-y-1">
        {backTo ? (
          <Button
            asChild
            variant="ghost"
            size="sm"
            className="text-muted-foreground -ml-2 h-7 px-2"
          >
            <Link {...backTo}>
              <ArrowLeft aria-hidden />
              {backLabel}
            </Link>
          </Button>
        ) : null}
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="truncate text-2xl font-semibold tracking-tight">{title}</h1>
          {meta}
          <TourButton />
        </div>
        {description ? <p className="text-muted-foreground text-sm">{description}</p> : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </header>
  );
}
